import { createContext, useEffect, useContext, useReducer } from "react";
import NotyfContext from "../contexts/NotyfContext.js";
import axios from "axios";
import { clearEventsState } from "../redux/eventsStorage";
import useAppDispatch from "../hooks/useAppDispatch.js";
import useAppSelector from "../hooks/useAppSelector.js";
import {
  resetEventsForUser,
  selectEvents,
  setEventsOwner,
} from "../redux/slices/events.js";

const PANEL_SESSION_HEARTBEAT_MS = 3 * 60 * 1000;

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user_id: null,
};

const PANEL_SESSION_ERROR_CODES = new Set([
  "SESSION_INVALID",
  "SESSION_REVOKED",
  "SESSION_EXPIRED",
]);

const resetPanelSession = (dispatch) => {
  clearEventsState();
  dispatch({
    type: "INITIALIZE",
    payload: initialState,
  });
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
};

const JWTReducer = (state, action) => {
  switch (action.type) {
    case "INITIALIZE":
      return {
        isAuthenticated: action.payload.isAuthenticated,
        isInitialized: true,
        user_id: action.payload.user_id,
        email: action.payload.email,
        nome: action.payload.nome,
        tipo: action.payload.tipo,
        plataforma_aplicativos: action.payload.plataforma_aplicativos,
        plataforma_eventos: action.payload.plataforma_eventos,
        aplicativos_customizacao: action.payload.aplicativos_customizacao,
      };
    case "SIGN_IN":
      return {
        ...state,
        isAuthenticated: true,
        isInitialized: true,
        user_id: action.payload.user_id,
        email: action.payload.email,
        nome: action.payload.nome,
        tipo: action.payload.tipo,
        plataforma_aplicativos: action.payload.plataforma_aplicativos,
        plataforma_eventos: action.payload.plataforma_eventos,
        aplicativos_customizacao: action.payload.aplicativos_customizacao,
      };

    default:
      return state;
  }
};

const AuthContext = createContext(null);

function AuthProvider({ children }) {
  const notyf = useContext(NotyfContext);
  const [state, dispatch] = useReducer(JWTReducer, initialState);
  const reduxDispatch = useAppDispatch();
  const eventsState = useAppSelector(selectEvents);

  useEffect(() => {
    const signInUser = async () => {
      try {
        await signIn();
      } catch (error) {
        notyf.open({
          type: "danger",
          message: error,
          ripple: true,
          dismissible: true,
        });
      }
    };

    signInUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        const code = error?.response?.data?.code;
        const isHeartbeat = error?.config?.r1TelemetryCategory === "session-heartbeat";
        if (PANEL_SESSION_ERROR_CODES.has(code) && !isHeartbeat) {
          resetPanelSession(dispatch);
        }
        return Promise.reject(error);
      },
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  useEffect(() => {
    if (state.isInitialized) {
      window.__R1_LOGIN_LOADING_READY__ = true;
      window.dispatchEvent(new CustomEvent('r1eventech:login-loader-ready'));
    }
  }, [state.isInitialized]);

  useEffect(() => {
    if (!state.isAuthenticated) return undefined;

    let heartbeatInFlight = false;
    const sendHeartbeat = () => {
      if (heartbeatInFlight) return;
      heartbeatInFlight = true;
      axios.post(
        "/api/session/heartbeat",
        {},
        { r1TelemetryCategory: "session-heartbeat" },
      )
        .catch((error) => {
          const code = error?.response?.data?.code;
          if (PANEL_SESSION_ERROR_CODES.has(code)) {
            resetPanelSession(dispatch);
          }
        })
        .finally(() => {
          heartbeatInFlight = false;
        });
    };

    sendHeartbeat();
    const heartbeatTimer = window.setInterval(sendHeartbeat, PANEL_SESSION_HEARTBEAT_MS);
    return () => window.clearInterval(heartbeatTimer);
  }, [state.isAuthenticated]);

  const signIn = async (email = null, password = null) => {
    await axios.post(
        "/api/signIn", {},
        {
          r1TelemetryCategory: email ? "api-network-failure" : "initialization-failure",
          auth: {
            username: email,
            password: password,
          },
        }
      )
      .then(function (response) {
        if (response.status === 204) {
          if (!state.isInitialized) {
            dispatch({
              type: "INITIALIZE",
              payload: initialState, // Entire array as the payload
            });
          }
          return
        } else {
          const nextUserId = response.data?.message?.user_id;
          const cachedUserId = eventsState.ownerUserId;
          const isSameCachedUser =
            cachedUserId !== null &&
            cachedUserId !== undefined &&
            nextUserId !== null &&
            nextUserId !== undefined &&
            String(cachedUserId) === String(nextUserId);

          if (!isSameCachedUser) {
            clearEventsState();
            reduxDispatch(resetEventsForUser());
          }
          reduxDispatch(setEventsOwner(nextUserId));
          dispatch({
            type: "SIGN_IN",
            payload: response.data.message,
          });
        }
      })
      .catch(function (error) {
        if (!state.isInitialized) {
          dispatch({
            type: "INITIALIZE",
            payload: initialState, // Entire array as the payload
          });
        } else {
          const status = error.response?.status;
          const code = error.response?.data?.code;
          const serverUnavailable = !error.response || status >= 500;
          if (serverUnavailable) {
            throw "Não foi possível conectar ao servidor do painel. Verifique se ele está online e tente novamente.";
          }
          if (status === 409 && code === "SESSION_ALREADY_ACTIVE") {
            throw "Este usuário já está conectado em outro dispositivo.";
          }
          if (status === 400 || status === 401) {
            throw error.response?.data?.message || "Não foi possível iniciar a sessão.";
          }
          throw error.response?.data?.message || "Não foi possível iniciar a sessão.";
        }
      });
  };

  const signOut = async () => {
    clearEventsState();
    await axios.post("/api/signOut")
      .then(function (response) {
        window.location.replace("/");
      })
      .catch(function (error) {
        if (error.response?.data?.code === "SESSION_REVOKE_UNAVAILABLE") {
          notyf.open({
            type: "danger",
            message: "Não foi possível encerrar a sessão. Tente novamente.",
            ripple: true,
            dismissible: true,
          });
          return;
        }
        dispatch({
          type: "INITIALIZE",
          payload: initialState, // Entire array as the payload
        });
      });
  };

  const resetPassword = (email) => console.log(email);
  return (
    <AuthContext.Provider
      value={{
        ...state,
        signIn,
        signOut,
        resetPassword,
      }}>
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
